import fromPairs from 'lodash/fromPairs';
import get from 'lodash/get';
import pick from 'lodash/pick';
import round from 'lodash/round';
import sortBy from 'lodash/sortBy';
import sumBy from 'lodash/sumBy';

import {
  calculateHeaderDiscount,
  calculateSpendableBudgetFromRawValues,
} from '../../quote-editor/utils/calculate-derived-values';
import { WRITABLE_BUDGET_FIELDS } from './constants';

function getWeights(line, lineCount, totals) {
  return fromPairs(
    WRITABLE_BUDGET_FIELDS.map(field => [
      field,
      totals[field] === 0 ? 1 / lineCount : line[field] / totals[field],
    ]),
  );
}

export function applyWeightedDiffs(lines, diff) {
  const lineCount = lines.length;

  const totals = fromPairs(
    WRITABLE_BUDGET_FIELDS.map(field => [field, sumBy(lines, field)]),
  );

  const weights = fromPairs(
    lines.map(line => [line.id, getWeights(line, lineCount, totals)]),
    'id',
  );

  const updates = lines.map(line =>
    pick(line, ['id', ...WRITABLE_BUDGET_FIELDS]),
  );

  WRITABLE_BUDGET_FIELDS.forEach(field => {
    const [min, ...rest] = sortBy(updates, ({ id }) => weights[id][field]);

    // Evenly distribute diff amount among all but the lowest-weighted lines
    const remainder = rest.reduce((rem, update) => {
      const delta = round(diff[field] * weights[update.id][field], 2);
      update[field] += delta; // eslint-disable-line no-param-reassign
      return rem - delta;
    }, diff[field]);

    // Line with the lowest weight for the field, is assigned the remaining amount to avoid rounding errors
    min[field] += round(remainder, 2);

    // In some cases, the value for field goes below 0 such as -0.01. This defaults those fields to 0 so that
    // the request doesnt fail.
    min[field] = min[field] > 0 ? min[field] : 0;
  });

  return updates;
}

export function getDiff(newValues, oldValues) {
  return fromPairs(
    WRITABLE_BUDGET_FIELDS.map(field => [
      field,
      newValues[field] - oldValues[field],
    ]),
  );
}

export function getProductRowId(line) {
  return `${line.product.id}-${line.adjustedFee || 0}`;
}

function calculateBudget(productAggregate, campaign, field = 'newValues') {
  const { headerDiscountPercent } = campaign;
  const {
    adjustedFee,
    [field]: { netClientBudgetExternal, wkzBudget, coInvest, freeService },
  } = productAggregate;
  const netClientBudget = netClientBudgetExternal + wkzBudget;
  return calculateSpendableBudgetFromRawValues(
    netClientBudget,
    headerDiscountPercent,
    coInvest,
    adjustedFee,
    freeService,
  );
}

export const calculateBudgetFromOldValues = (productAggregate, campaign) =>
  calculateBudget(productAggregate, campaign, 'oldValues');

export const calculateBudgetFromNewValues = (productAggregate, campaign) =>
  calculateBudget(productAggregate, campaign, 'newValues');

export function getBudgetDiffs(lines, diffKey) {
  return lines.map(line => {
    const {
      netClientBudgetExternal,
      wkzBudget,
      coInvest,
      adjustedFee,
      freeService,
      spendableBudget,
    } = line;
    const netClientBudget = netClientBudgetExternal + wkzBudget;
    const headerDiscount = calculateHeaderDiscount(
      spendableBudget,
      netClientBudget,
      coInvest,
      freeService,
      adjustedFee,
    );
    const draftValue = calculateSpendableBudgetFromRawValues(
      netClientBudget,
      headerDiscount,
      coInvest,
      adjustedFee,
      freeService,
    );
    const originValue = calculateSpendableBudgetFromRawValues(
      get(
        line,
        [diffKey, 'netClientBudgetExternal', 'originValue'],
        netClientBudgetExternal,
      ) + get(line, [diffKey, 'wkzBudget', 'originValue'], wkzBudget),
      headerDiscount,
      get(line, [diffKey, 'coInvest', 'originValue'], coInvest),
      adjustedFee,
      get(line, [diffKey, 'freeService', 'originValue'], freeService),
    );
    return { draftValue, originValue };
  });
}

export const hasDeltaChanged = (productAggregates, field) =>
  productAggregates.some(
    product => product.newValues[field] !== product.oldValues[field],
  );
